import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Browser } from '@capacitor/browser';
import { Capacitor } from '@capacitor/core';
import { Subscription } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { AuthService } from '../../../core/services/auth.service';
import {
  ImagePricingService,
  ImagePricingTier,
} from '../../../core/services/image-pricing.service';
import { ImageUsageService } from '../../../core/services/image-usage.service';
import { PricingService } from '../../../core/services/pricing.service';
import {
  PaymentResult,
  StripePaymentService,
} from '../../../core/services/stripe-payment.service';
import { ToastService } from '../../../core/services/toast.service';
import {
  TranslationKeys,
  TranslationService,
} from '../../../core/services/translation.service';
import { WordUsageService } from '../../../core/services/word-usage.service';

export interface WordPackage {
  id: string;
  name: string;
  words: number;
  price?: number;
  type?: 'one-time' | 'monthly' | 'yearly';
  originalPrice?: number;
  savings?: number;
  popular?: boolean;
  description?: string;
}

@Component({
  selector: 'app-buy-words-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './buy-words-modal.component.html',
  styleUrls: ['./buy-words-modal.component.css'],
})
export class BuyWordsModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() preSelectedPackageId: string | null = null;
  @Input() preSelectedImagePackageId: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() wordsPurchased = new EventEmitter<number>();
  @Output() imagesPurchased = new EventEmitter<number>();
  @Output() paymentSuccessMessage = new EventEmitter<string>();

  selectedWordPackage: WordPackage | null = null;
  selectedImagePackage: ImagePricingTier | null = null;
  isLoading = false;
  isProcessingPayment = false;
  showPaymentForm = false;
  paymentError: string | null = null;
  translations: TranslationKeys = {} as TranslationKeys;
  currentUserEmail: string | null = null;
  private userSubscription?: Subscription;

  constructor(
    private wordUsageService: WordUsageService,
    private imageUsageService: ImageUsageService,
    private pricingService: PricingService,
    private imagePricingService: ImagePricingService,
    private stripePaymentService: StripePaymentService,
    private authService: AuthService,
    private translationService: TranslationService,
    private cdr: ChangeDetectorRef,
    private toastService: ToastService
  ) {}

  wordPackages: WordPackage[] = [];
  imagePackages: ImagePricingTier[] = [];

  ngOnInit(): void {
    // Load pricing tiers from both services
    this.wordPackages = this.pricingService.getPricingTiers();
    this.imagePackages = this.imagePricingService.getImagePricingTiers();
    console.log('📦 Buy Words Modal initialized (merged with images)');
    console.log('📦 Loaded word packages:', this.wordPackages.length);
    console.log('📦 Loaded image packages:', this.imagePackages.length);
    console.log(
      '📦 Word package details:',
      this.wordPackages.map((p) => ({ id: p.id, name: p.name, price: p.price }))
    );
    console.log(
      '📦 Image package details:',
      this.imagePackages.map((p) => ({
        id: p.id,
        name: p.name,
        price: p.price,
      }))
    );

    // Get current user email
    const currentUser = this.authService.getCurrentUser();
    if (currentUser && currentUser.email) {
      this.currentUserEmail = currentUser.email;
      console.log('📧 Current user email:', this.currentUserEmail);
    }

    // Subscribe to user changes
    this.userSubscription = this.authService.currentUser$.subscribe((user) => {
      if (user && user.email) {
        this.currentUserEmail = user.email;
        this.cdr.markForCheck();
      } else {
        this.currentUserEmail = null;
        this.cdr.markForCheck();
      }
    });

    // Subscribe to translations
    this.translationService.currentLanguage$.subscribe((language) => {
      this.translations = this.translationService.getAllTranslations();
    });

    // Initialize Stripe early for better UX
    this.stripePaymentService.initializeStripe().catch((error) => {
      console.error('❌ Failed to initialize Stripe:', error);
    });

    // Auto-select package(s) if pre-selected from URL
    if (this.preSelectedPackageId || this.preSelectedImagePackageId) {
      console.log('🎯 Pre-selected package IDs:', {
        wordPackageId: this.preSelectedPackageId,
        imagePackageId: this.preSelectedImagePackageId,
      });
      setTimeout(() => {
        // Pre-select word package if specified
        if (this.preSelectedPackageId) {
          const isWordPackage = this.wordPackages.some(
            (p) => p.id === this.preSelectedPackageId
          );
          if (isWordPackage) {
            this.onWordPackageSelect(this.preSelectedPackageId!);
            console.log(
              '✅ Pre-selected word package:',
              this.preSelectedPackageId
            );
          }
        }

        // Pre-select image package if specified
        if (this.preSelectedImagePackageId) {
          const isImagePackage = this.imagePackages.some(
            (p) => p.id === this.preSelectedImagePackageId
          );
          if (isImagePackage) {
            this.onImagePackageSelect(this.preSelectedImagePackageId!);
            console.log(
              '✅ Pre-selected image package:',
              this.preSelectedImagePackageId
            );
          }
        }
      }, 100);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Handle when modal opens - pre-select both word and image packages if provided
    if (
      changes['isOpen'] &&
      changes['isOpen'].currentValue === true &&
      (this.preSelectedPackageId || this.preSelectedImagePackageId)
    ) {
      console.log('📦 Modal opened with pre-selected packages:', {
        wordPackageId: this.preSelectedPackageId,
        imagePackageId: this.preSelectedImagePackageId,
      });
      setTimeout(() => {
        // Pre-select word package if specified
        if (this.preSelectedPackageId) {
          const isWordPackage = this.wordPackages.some(
            (p) => p.id === this.preSelectedPackageId
          );
          if (isWordPackage) {
            this.onWordPackageSelect(this.preSelectedPackageId);
          } else {
            // Fallback to legacy onPackageSelect for backward compatibility
            this.onPackageSelect(this.preSelectedPackageId);
          }
        }
        // Pre-select image package if specified
        if (this.preSelectedImagePackageId) {
          const isImagePackage = this.imagePackages.some(
            (p) => p.id === this.preSelectedImagePackageId
          );
          if (isImagePackage) {
            this.onImagePackageSelect(this.preSelectedImagePackageId);
          } else {
            console.warn(
              '⚠️ Pre-selected image package ID not found:',
              this.preSelectedImagePackageId
            );
          }
        }
      }, 100);
    }

    // Handle when preSelectedPackageId changes after modal is already open
    if (
      changes['preSelectedPackageId'] &&
      changes['preSelectedPackageId'].currentValue &&
      !changes['isOpen'] // Don't handle if this is part of modal opening
    ) {
      console.log(
        '🎯 Pre-selected word package changed:',
        changes['preSelectedPackageId'].currentValue
      );
      setTimeout(() => {
        this.onWordPackageSelect(changes['preSelectedPackageId'].currentValue);
      }, 100);
    }

    // Handle when preSelectedImagePackageId changes after modal is already open
    if (
      changes['preSelectedImagePackageId'] &&
      changes['preSelectedImagePackageId'].currentValue &&
      !changes['isOpen'] // Don't handle if this is part of modal opening
    ) {
      console.log(
        '🎯 Pre-selected image package changed:',
        changes['preSelectedImagePackageId'].currentValue
      );
      setTimeout(() => {
        this.onImagePackageSelect(
          changes['preSelectedImagePackageId'].currentValue
        );
      }, 100);
    }
  }

  ngOnDestroy(): void {
    // Cleanup subscriptions
    if (this.userSubscription) {
      this.userSubscription.unsubscribe();
    }
    // Clean up Stripe elements
    this.stripePaymentService.cleanup();
  }

  // Get all packages
  getAllPackages(): WordPackage[] {
    return this.wordPackages;
  }

  // Check if current user is admin
  isAdmin(): boolean {
    return this.authService.isAdmin();
  }

  // Get button text based on user role and state
  getButtonText(): string {
    if (this.isProcessingPayment) {
      return this.isAdmin() ? 'Adding...' : 'Processing...';
    }

    if (!this.hasSelection()) {
      return this.translations.selectAnOption || 'Select a package';
    }

    const totalPrice = this.getTotalPrice();
    if (this.isAdmin()) {
      const parts: string[] = [];
      if (this.selectedWordPackage) {
        parts.push(`${this.selectedWordPackage.words.toLocaleString()} Words`);
      }
      if (this.selectedImagePackage) {
        parts.push(
          `${this.selectedImagePackage.images.toLocaleString()} Images`
        );
      }
      return `Add ${parts.join(' + ')} (Free)`;
    }

    return `Purchase ${this.formatPrice(totalPrice)}`;
  }

  // Format price for display
  formatPrice(price: number): string {
    return this.pricingService.formatPrice(price);
  }

  // Debug method to show pricing calculation (can be removed in production)
  getPricingDetails(): any {
    return this.pricingService.getPricingDetails();
  }

  onClose(): void {
    console.log('❌ Close button clicked');
    console.log('❌ Current state - showPaymentForm:', this.showPaymentForm);
    console.log(
      '❌ Current state - isProcessingPayment:',
      this.isProcessingPayment
    );
    console.log('❌ Emitting close event');

    // Reset all states
    this.showPaymentForm = false;
    this.paymentError = null;
    this.isProcessingPayment = false;

    // Clean up Stripe elements
    this.stripePaymentService.cleanup();

    // Emit close event
    this.close.emit();
  }

  onWordPackageSelect(packageId: string): void {
    console.log('🎯 Word package selected:', packageId);
    const selected = this.wordPackages.find((p) => p.id === packageId);
    if (selected) {
      // Toggle selection - if same package clicked, deselect it
      if (this.selectedWordPackage?.id === packageId) {
        this.selectedWordPackage = null;
        console.log('🔄 Word package deselected');
      } else {
        this.selectedWordPackage = selected;
        console.log('✅ Word package selected:', selected);
      }
    } else {
      console.error('❌ Word package not found:', packageId);
    }
    this.cdr.detectChanges();
  }

  onImagePackageSelect(packageId: string): void {
    console.log('🎯 Image package selected:', packageId);
    const selected = this.imagePackages.find((p) => p.id === packageId);
    if (selected) {
      // Toggle selection - if same package clicked, deselect it
      if (this.selectedImagePackage?.id === packageId) {
        this.selectedImagePackage = null;
        console.log('🔄 Image package deselected');
      } else {
        this.selectedImagePackage = selected;
        console.log('✅ Image package selected:', selected);
      }
    } else {
      console.error('❌ Image package not found:', packageId);
    }
    this.cdr.detectChanges();
  }

  // Legacy method for backward compatibility
  onPackageSelect(packageId: string): void {
    // Check if it's a word or image package
    const isWordPackage = this.wordPackages.some((p) => p.id === packageId);
    const isImagePackage = this.imagePackages.some((p) => p.id === packageId);
    if (isWordPackage) {
      this.onWordPackageSelect(packageId);
    } else if (isImagePackage) {
      this.onImagePackageSelect(packageId);
    }
  }

  getTotalPrice(): number {
    let total = 0;
    if (this.selectedWordPackage?.price) {
      total += this.selectedWordPackage.price;
    }
    if (this.selectedImagePackage?.price) {
      total += this.selectedImagePackage.price;
    }
    return total;
  }

  hasSelection(): boolean {
    return !!this.selectedWordPackage || !!this.selectedImagePackage;
  }

  async onAddWords(): Promise<void> {
    console.log('🛒 Purchase button clicked');
    console.log('📦 Selected word package:', this.selectedWordPackage);
    console.log('📦 Selected image package:', this.selectedImagePackage);
    console.log('⏳ Loading state:', this.isLoading);
    console.log('🔍 AuthService available:', !!this.authService);
    console.log(
      '🔍 StripePaymentService available:',
      !!this.stripePaymentService
    );

    if (!this.hasSelection()) {
      console.log('❌ No package selected');
      this.paymentError = 'Please select at least one package.';
      return;
    }

    const totalPrice = this.getTotalPrice();
    if (totalPrice <= 0) {
      console.error('❌ Total price is invalid');
      this.paymentError = 'Invalid package selection. Please try again.';
      return;
    }

    // Check if user is admin - skip payment for admins
    console.log('🔍 Checking if user is admin...');
    const isAdmin = this.authService.isAdmin();
    console.log('🔍 Is admin:', isAdmin);

    if (isAdmin) {
      console.log(
        '👑 Admin user detected - skipping payment and adding packages directly'
      );
      await this.adminDirectPurchase();
      return;
    }

    try {
      this.isProcessingPayment = true;
      this.paymentError = null;

      // Calculate amount in cents
      const amountInCents = Math.round(totalPrice * 100);
      console.log('🔍 Total amount in cents:', amountInCents);

      // Use native PaymentSheet on mobile, Checkout Session on web
      console.log('🔍 Platform check:', {
        isNative: Capacitor.isNativePlatform(),
        platform: Capacitor.getPlatform(),
        hasWordPackage: !!this.selectedWordPackage,
        hasImagePackage: !!this.selectedImagePackage,
        amount: amountInCents,
      });

      if (Capacitor.isNativePlatform()) {
        console.log('📱 Mobile detected - redirecting to webapp for payment');
        console.log('🌐 Opening webapp in browser...');

        try {
          // Store buy parameters in sessionStorage before opening browser
          // This allows the app to detect them when user returns from browser
          const buyParams: any = {
            buy: 'combined', // New type for combined purchases
            price: totalPrice.toString(),
          };

          // Add word package params if selected
          if (this.selectedWordPackage?.price) {
            buyParams.wordPackageId = this.selectedWordPackage.id;
            buyParams.words = this.selectedWordPackage.words.toString();
            buyParams.wordPrice = this.selectedWordPackage.price.toString();
            console.log('✅ Added word params:', {
              wordPackageId: buyParams.wordPackageId,
              words: buyParams.words,
              wordPrice: buyParams.wordPrice,
            });
          }

          // Add image package params if selected (same pattern as words)
          if (this.selectedImagePackage && this.selectedImagePackage.price) {
            buyParams.imagePackageId = this.selectedImagePackage.id;
            buyParams.images = this.selectedImagePackage.images.toString();
            buyParams.imagePrice = this.selectedImagePackage.price.toString();
            console.log('✅ Added image params:', {
              imagePackageId: buyParams.imagePackageId,
              images: buyParams.images,
              imagePrice: buyParams.imagePrice,
              selectedImagePackage: this.selectedImagePackage,
            });
          } else {
            console.warn('⚠️ Image package not added to params:', {
              hasSelectedImagePackage: !!this.selectedImagePackage,
              hasPrice: !!(
                this.selectedImagePackage && this.selectedImagePackage.price
              ),
              selectedImagePackage: this.selectedImagePackage,
            });
          }

          console.log(
            '💾 Storing buy parameters in sessionStorage for mobile:',
            buyParams
          );
          sessionStorage.setItem(
            'pending_buy_action',
            JSON.stringify(buyParams)
          );

          // Open webapp URL for payment (avoids app store restrictions)
          // Use dev URL if in dev mode, otherwise use prod URL
          const webappUrl = !environment.production
            ? 'https://translation-app-dev-648c3.web.app'
            : 'https://tilhub.net';

          // Convert buyParams object to URLSearchParams (URLSearchParams doesn't accept plain objects directly)
          const packageParams = new URLSearchParams();
          Object.keys(buyParams).forEach((key) => {
            if (buyParams[key] !== undefined && buyParams[key] !== null) {
              packageParams.append(key, String(buyParams[key]));
              console.log(`🔗 Added URL param: ${key} = ${buyParams[key]}`);
            } else {
              console.warn(`⚠️ Skipping null/undefined param: ${key}`);
            }
          });

          const fullUrl = `${webappUrl}/?${packageParams.toString()}`;
          console.log('🔗 Opening webapp URL:', fullUrl);
          console.log('🔗 URL parameters:', packageParams.toString());
          console.log('🔗 Buy params object:', buyParams);
          console.log('🔗 Verifying images param in URL:', {
            hasImagesInParams: packageParams.has('images'),
            imagesValue: packageParams.get('images'),
            allParams: Array.from(packageParams.entries()),
          });

          // Open in system browser
          await Browser.open({ url: fullUrl, windowName: '_blank' });

          // Show info message to user
          this.toastService.showInfo(
            'Please complete payment in the browser, then return to the app.',
            5000
          );

          // Close the modal after opening browser
          this.isProcessingPayment = false;
          this.close.emit();

          // Don't wait for payment confirmation - user will complete on webapp
          return;
        } catch (error) {
          console.error('❌ Error opening webapp:', error);
          this.paymentError = 'Failed to open payment page';
          this.isProcessingPayment = false;
        }
      } else {
        // For web, create Stripe Checkout Session
        console.log('🌐 Web detected - creating Stripe checkout session');

        // Determine package type and ID for checkout
        // If both selected, prioritize words for now (backend needs update for combined)
        let checkoutPackageId: string;
        let checkoutPackageType: 'words' | 'images';

        if (this.selectedWordPackage && this.selectedImagePackage) {
          // Both selected - use words as primary, image info will be in metadata
          checkoutPackageId = this.selectedWordPackage.id;
          checkoutPackageType = 'words';
          console.log(
            '📦 Both packages selected - using words package for checkout'
          );
        } else if (this.selectedWordPackage) {
          checkoutPackageId = this.selectedWordPackage.id;
          checkoutPackageType = 'words';
        } else if (this.selectedImagePackage) {
          checkoutPackageId = this.selectedImagePackage.id;
          checkoutPackageType = 'images';
        } else {
          this.paymentError = 'No package selected';
          this.isProcessingPayment = false;
          return;
        }

        console.log(
          `💳 Starting hosted checkout for ${checkoutPackageType} package: ${checkoutPackageId}, total: $${totalPrice}`
        );

        const checkoutItems: Array<{
          packageId: string;
          packageType: 'words' | 'images';
        }> = [];
        if (this.selectedWordPackage) {
          checkoutItems.push({
            packageId: this.selectedWordPackage.id,
            packageType: 'words',
          });
        }
        if (this.selectedImagePackage) {
          checkoutItems.push({
            packageId: this.selectedImagePackage.id,
            packageType: 'images',
          });
        }

        // Create checkout session and redirect to Stripe
        console.log('🔍 Calling stripePaymentService.createCheckoutSession...');
        this.stripePaymentService
          .createCheckoutSession(
            checkoutPackageId,
            checkoutPackageType,
            amountInCents,
            'usd',
            checkoutItems
          )
          .subscribe({
            next: (response) => {
              console.log('✅ Checkout session created:', response);
              console.log('🔍 Response type:', typeof response);
              console.log('🔍 Response keys:', Object.keys(response || {}));

              if (response && response.success && response.checkoutUrl) {
                console.log(
                  '🔍 Redirecting to checkout URL:',
                  response.checkoutUrl
                );

                // Store combined purchase info in sessionStorage for webhook handling
                if (this.selectedWordPackage && this.selectedImagePackage) {
                  const combinedInfo = {
                    wordPackageId: this.selectedWordPackage.id,
                    words: this.selectedWordPackage.words,
                    wordPrice: this.selectedWordPackage.price,
                    imagePackageId: this.selectedImagePackage.id,
                    images: this.selectedImagePackage.images,
                    imagePrice: this.selectedImagePackage.price,
                    sessionId: response.sessionId || '',
                  };
                  sessionStorage.setItem(
                    'combined_purchase',
                    JSON.stringify(combinedInfo)
                  );
                  console.log(
                    '💾 Stored combined purchase info:',
                    combinedInfo
                  );
                }

                // Use sessionId from response (backend returns it)
                const sessionId = response.sessionId || '';

                // Redirect to Stripe Checkout
                this.stripePaymentService
                  .redirectToCheckout(
                    response.checkoutUrl,
                    sessionId,
                    checkoutPackageId,
                    checkoutPackageType
                  )
                  .catch((error) => {
                    console.error('❌ Failed to redirect to checkout:', error);
                    this.paymentError = 'Failed to open payment page';
                    this.isProcessingPayment = false;
                  });
              } else {
                console.error(
                  '❌ Invalid checkout session response:',
                  response
                );
                this.paymentError = 'Failed to create checkout session';
                this.isProcessingPayment = false;
              }
            },
            error: (error) => {
              console.error('❌ Error creating checkout session:', error);
              console.error('❌ Error details:', {
                status: error.status,
                statusText: error.statusText,
                message: error.message,
                url: error.url,
              });
              this.paymentError = 'Failed to start payment process';
              this.isProcessingPayment = false;

              // Fallback to direct purchase if Stripe is not configured
              if (error.status === 503) {
                console.log('⚠️ Stripe not configured - using fallback');
                this.fallbackPurchase();
              }
            },
          });
      }
    } catch (error) {
      console.error('❌ Payment process error:', error);
      this.paymentError = 'An unexpected error occurred';
      this.isProcessingPayment = false;
    }
  }

  /**
   * Admin direct purchase method - skips payment for admin users
   */
  private async adminDirectPurchase(): Promise<void> {
    if (!this.hasSelection()) return;

    try {
      this.isProcessingPayment = true;
      console.log('👑 Admin direct purchase - adding packages without payment');

      const promises: Promise<any>[] = [];

      // Purchase words if selected
      if (this.selectedWordPackage) {
        console.log('👑 Adding words:', this.selectedWordPackage.words);
        const wordPromise = this.wordUsageService
          .purchaseWords(
            this.selectedWordPackage.words,
            this.selectedWordPackage.type || 'one-time',
            this.selectedWordPackage.id,
            this.selectedWordPackage.price
          )
          .toPromise();
        promises.push(wordPromise);
      }

      // Purchase images if selected
      if (this.selectedImagePackage) {
        console.log('👑 Adding images:', this.selectedImagePackage.images);
        const imagePromise = this.imageUsageService.purchaseImages(
          this.selectedImagePackage.images
        );
        promises.push(imagePromise);
      }

      // Wait for all purchases to complete
      await Promise.all(promises);

      console.log('✅ All packages added successfully for admin');
      this.isProcessingPayment = false;

      // Emit the purchased amounts
      if (this.selectedWordPackage) {
        this.wordsPurchased.emit(this.selectedWordPackage.words);
      }
      if (this.selectedImagePackage) {
        this.imagesPurchased.emit(this.selectedImagePackage.images);
      }

      // Close the modal
      this.onClose();
    } catch (error) {
      console.error('❌ Admin direct purchase error:', error);
      this.isProcessingPayment = false;
      this.paymentError = 'Failed to add packages. Please try again.';
    }
  }

  /**
   * Fallback purchase method when Stripe is not configured
   */
  private async fallbackPurchase(): Promise<void> {
    if (!this.hasSelection()) return;

    try {
      console.log('🔄 Using fallback purchase method');

      const promises: Promise<any>[] = [];

      // Purchase words if selected
      if (this.selectedWordPackage) {
        const wordPromise = this.wordUsageService
          .purchaseWords(
            this.selectedWordPackage.words,
            this.selectedWordPackage.type || 'one-time',
            this.selectedWordPackage.id,
            this.selectedWordPackage.price
          )
          .toPromise();
        promises.push(wordPromise);
      }

      // Purchase images if selected
      if (this.selectedImagePackage) {
        const imagePromise = this.imageUsageService.purchaseImages(
          this.selectedImagePackage.images
        );
        promises.push(imagePromise);
      }

      await Promise.all(promises);

      console.log('✅ Packages purchased successfully (fallback)');
      this.isProcessingPayment = false;

      // Emit the purchased amounts
      if (this.selectedWordPackage) {
        this.wordsPurchased.emit(this.selectedWordPackage.words);
      }
      if (this.selectedImagePackage) {
        this.imagesPurchased.emit(this.selectedImagePackage.images);
      }

      // Close the modal
      this.onClose();
    } catch (error) {
      console.error('❌ Fallback purchase error:', error);
      this.isProcessingPayment = false;
      this.paymentError = 'Purchase failed. Please try again.';
    }
  }

  async onConfirmPayment(): Promise<void> {
    if (!this.hasSelection()) {
      return;
    }

    try {
      this.isProcessingPayment = true;
      this.paymentError = null;

      console.log('💳 Confirming payment...');

      // Confirm payment with Stripe
      const result: PaymentResult =
        await this.stripePaymentService.confirmPayment();

      if (!result.success) {
        throw new Error(result.error || 'Payment failed');
      }

      console.log('✅ Payment confirmed:', result.paymentIntentId);

      // TODO: Update backend to handle combined payment confirmation
      // For now, handle words only (images will be handled separately)
      if (this.selectedWordPackage) {
        const confirmResponse = await this.wordUsageService
          .confirmStripePayment(result.paymentIntentId!)
          .toPromise();

        if (!confirmResponse?.success) {
          throw new Error('Failed to confirm payment on server');
        }

        console.log('✅ Payment fully confirmed and words added');
        this.wordsPurchased.emit(this.selectedWordPackage.words);
      }

      // TODO: Handle image purchase confirmation
      if (this.selectedImagePackage) {
        // This will need backend support for combined purchases
        console.log(
          '⚠️ Image purchase confirmation not yet implemented for combined purchases'
        );
      }

      // Close the modal
      this.onClose();
    } catch (error) {
      console.error('❌ Error confirming payment:', error);
      this.paymentError =
        error instanceof Error
          ? error.message
          : 'Payment failed. Please try again.';
      this.isProcessingPayment = false;
    }
  }

  onBackToPackages(): void {
    this.showPaymentForm = false;
    this.paymentError = null;
    this.stripePaymentService.cleanup();
  }

  onOverlayClick(event: Event): void {
    if (event.target === event.currentTarget) {
      this.onClose();
    }
  }
}
